/**
 * Wire protocol between client and server, with zod schemas for everything the server accepts.
 *
 * REST (JSON):
 *   POST /api/matches                 CreateMatchRequest  -> CreateMatchResponse
 *   GET  /api/matches/:code           -> MatchSummary (public lobby info, no hidden state)
 *   POST /api/matches/:code/join      JoinMatchRequest    -> JoinMatchResponse
 *   POST /api/matches/:code/start     (host token)        -> { ok: true }
 *   GET  /api/matches/:code/map       (player token)      -> GameMap (static terrain)
 *   DELETE /api/join-requests/:id     (request token) withdraw a pending late-join request
 *   GET  /api/push/public-key         -> { publicKey }
 *   POST /api/push/subscribe          PushSubscribeRequest (player token)
 *   DELETE /api/push/subscribe        (player token)
 *   POST /api/matches/:code/transfer  (player token)      -> CreateTransferResponse (own seat)
 *   POST /api/matches/:code/players/:playerId/transfer (host token) -> CreateTransferResponse
 *   POST /api/transfer/claim          ClaimTransferRequest -> ClaimTransferResponse
 *
 * Auth: no accounts. Joining returns a secret `playerToken`, sent as `Authorization: Bearer <token>`
 * and in the first WebSocket message. The client keeps it in localStorage per match. A one-time
 * transfer link (15 minutes, single use) mints an extra token for the same player on a new device.
 *
 * WebSocket: GET /ws, then send `{t:'auth', token}` (or `{t:'auth', requestToken}` for the
 * join-request socket) within 5 s. `/ws?token=` / `?requestToken=` still work for older clients.
 * Other messages are ClientMessage / ServerMessage below.
 */
import { z } from 'zod';
import type { GameEvent, PlayerView } from './types';

const int = (min: number, max: number) => z.number().int().min(min).max(max);

export const axialSchema = z.object({ q: int(-1000, 1000), r: int(-1000, 1000) });

export const characterSchema = z.object({
  body: int(0, 63),
  hair: int(0, 63),
  beard: int(0, 63),
  outfit: int(0, 63),
  legs: int(0, 63),
  headgear: int(0, 63),
  weapon: int(0, 63),
  shield: int(0, 63),
});

export const playerNameSchema = z
  .string()
  .trim()
  .min(2, 'Name must be at least 2 characters')
  .max(20, 'Name must be at most 20 characters')
  .regex(/^[\p{L}\p{N} _'.-]+$/u, "Use letters, numbers, spaces and - _ . ' only");

export const matchConfigSchema = z.object({
  name: z.string().trim().min(2).max(40),
  mode: z.literal('relic-hunt'),
  teamSlots: z.tuple([int(1, 5), int(1, 5)]),
  durationDays: int(1, 14),
  timeScale: z.number().min(1).max(1440),
  fillWithAi: z.boolean(),
});

export const teamIdSchema = z.union([z.literal(0), z.literal(1)]);

export const createMatchRequestSchema = z.object({
  config: matchConfigSchema,
  host: z.object({ name: playerNameSchema, character: characterSchema, teamId: teamIdSchema }),
});
export type CreateMatchRequest = z.infer<typeof createMatchRequestSchema>;
export interface CreateMatchResponse {
  code: string;
  matchId: string;
  playerId: string;
  playerToken: string;
}

export const joinMatchRequestSchema = z.object({
  name: playerNameSchema,
  character: characterSchema,
  teamId: teamIdSchema,
});
export type JoinMatchRequest = z.infer<typeof joinMatchRequestSchema>;
export type JoinMatchResponse =
  | { status: 'joined'; playerId: string; playerToken: string }
  /** Match already running: a teammate must approve. Poll or wait on the request socket. */
  | { status: 'pending'; requestId: string; requestToken: string };

export interface MatchSummary {
  code: string;
  name: string;
  status: 'lobby' | 'running' | 'finished';
  config: z.infer<typeof matchConfigSchema>;
  teams: {
    id: 0 | 1;
    name: string;
    slots: number;
    players: {
      /** Stable player id (not a secret — tokens are). */
      id: string;
      name: string;
      isAi: boolean;
      isHost: boolean;
      character: z.infer<typeof characterSchema>;
      online: boolean;
    }[];
  }[];
  day: number | null;
}

// --- Device transfer ("Play on another device") -----------------------------------------------

export interface CreateTransferResponse {
  /** Secret, single use. The client shows it as `/claim/<token>` and a QR code. */
  transferToken: string;
  /** Real time (ms since epoch) after which the link stops working. */
  expiresAt: number;
}

export const claimTransferRequestSchema = z.object({ transferToken: z.string().min(20).max(128) });
export type ClaimTransferRequest = z.infer<typeof claimTransferRequestSchema>;

export interface ClaimTransferResponse {
  code: string;
  playerId: string;
  /** A fresh token for this device; the player's other tokens stay valid. */
  playerToken: string;
  matchName: string;
  playerName: string;
}

export const pushSubscribeRequestSchema = z.object({
  endpoint: z.url(),
  keys: z.object({ p256dh: z.string().min(1), auth: z.string().min(1) }),
});

const unitCountsSchema = z.object({
  infantry: int(0, 1000),
  archers: int(0, 1000),
  cavalry: int(0, 1000),
  healers: int(0, 1000),
  scouts: int(0, 1000),
});
const doctrineSchema = z.enum(['bold', 'steady', 'careful']);
const triggerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('atTime'), time: z.number().int().min(0) }),
  z.object({ kind: z.literal('enemyWithin'), hexes: int(0, 6) }),
  z.object({ kind: z.literal('engaged') }),
  z.object({ kind: z.literal('allyEngagedWithin'), hexes: int(1, 6) }),
  z.object({ kind: z.literal('moraleBelow'), value: int(1, 99) }),
]);
const actionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('moveTo'), to: axialSchema }),
  z.object({ kind: z.literal('fallBackTo'), to: axialSchema }),
  z.object({ kind: z.literal('hold') }),
  z.object({ kind: z.literal('reinforce') }),
  z.object({ kind: z.literal('setDoctrine'), doctrine: doctrineSchema }),
]);
const id = z.string().min(1).max(64);
const via = z.enum(['auto', 'raven']).optional();

export const commandSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('moveArmy'), armyId: id, to: axialSchema, via }),
  z.object({ type: z.literal('retreat'), armyId: id, via }),
  z.object({ type: z.literal('setDoctrine'), armyId: id, doctrine: doctrineSchema, via }),
  z.object({
    type: z.literal('setConditionals'),
    armyId: id,
    orders: z.array(z.object({ trigger: triggerSchema, action: actionSchema })).max(3),
    via,
  }),
  z.object({
    type: z.literal('splitArmy'),
    armyId: id,
    units: unitCountsSchema,
    withHero: z.boolean(),
    name: z.string().trim().min(1).max(24).optional(),
  }),
  z.object({ type: z.literal('mergeArmies'), armyId: id, intoArmyId: id }),
  z.object({
    type: z.literal('recruit'),
    at: axialSchema,
    unit: z.enum(['infantry', 'archers', 'cavalry', 'healers', 'scouts']),
    count: int(1, 100),
  }),
  z.object({
    type: z.literal('build'),
    kind: z.enum(['road', 'bridge', 'watchtower']),
    at: axialSchema,
    armyId: id,
  }),
  z.object({ type: z.literal('demolish'), kind: z.enum(['road', 'bridge']), at: axialSchema, armyId: id }),
  z.object({ type: z.literal('castSpell'), spell: z.enum(['rally', 'farSight']), target: axialSchema }),
  z.object({ type: z.literal('pickUpRelic'), armyId: id, relicId: id }),
  z.object({ type: z.literal('dropRelic'), armyId: id, relicId: id }),
  z.object({ type: z.literal('channel'), on: z.boolean() }),
  z.object({ type: z.literal('approveJoin'), requestId: id }),
  z.object({ type: z.literal('rejectJoin'), requestId: id }),
  z.object({
    type: z.literal('ping'),
    at: axialSchema,
    kind: z.enum(['help', 'scout', 'escort', 'attack']),
    note: z.string().trim().max(80).optional(),
  }),
  z.object({ type: z.literal('chat'), text: z.string().trim().min(1).max(280) }),
  z.object({ type: z.literal('setQuietWindow'), startHour: int(0, 23).nullable() }),
  z.object({
    type: z.literal('setPreferences'),
    preferences: z
      .object({
        dispatchHour: int(0, 23),
        utcOffsetMinutes: int(-14 * 60, 14 * 60),
        alerts: z.object({
          heroEngaged: z.boolean(),
          relicChanged: z.boolean(),
          helpRequested: z.boolean(),
          joinRequests: z.boolean(),
          moraleLow: z.boolean(),
          messengerLost: z.boolean(),
        }),
      })
      .partial(),
  }),
  z.object({ type: z.literal('renameArmy'), armyId: id, name: z.string().trim().min(1).max(24) }),
  z.object({ type: z.literal('changeTeam'), teamId: teamIdSchema }),
  z.object({ type: z.literal('updateCharacter'), character: characterSchema }),
  z.object({ type: z.literal('voteConcede') }),
  z.object({ type: z.literal('withdrawConcede') }),
]);

// --- WebSocket messages -----------------------------------------------------------------------

export const clientMessageSchema = z.discriminatedUnion('t', [
  /** Every command carries a client-chosen id so the reply can be matched. */
  z.object({ t: z.literal('cmd'), id: z.string().min(1).max(40), cmd: commandSchema }),
  z.object({ t: z.literal('ping') }),
]);
export type ClientMessage = z.infer<typeof clientMessageSchema>;

/** First message on `/ws` (instead of a token in the URL): a player token or a join-request token. */
export const socketAuthMessageSchema = z.union([
  z.object({ t: z.literal('auth'), token: z.string().min(1).max(128) }),
  z.object({ t: z.literal('auth'), requestToken: z.string().min(1).max(128) }),
]);
export type SocketAuthMessage = z.infer<typeof socketAuthMessageSchema>;

export type ServerMessage =
  | { t: 'view'; view: PlayerView }
  | { t: 'events'; events: GameEvent[] }
  | { t: 'ack'; id: string; ok: true; eta?: number }
  | { t: 'ack'; id: string; ok: false; error: string }
  | { t: 'pong'; serverTimeMs: number }
  | { t: 'error'; error: string }
  /** Sent instead of `view` while the match is in its lobby and no view can be built yet. */
  | { t: 'lobby'; summary: MatchSummary; you: string }
  /** Request socket (`/ws?requestToken=`) only: the join request's status, then the decision. */
  | ({ t: 'joinRequest' } & JoinRequestStatusMessage);

/** Status of a late-join request, as polled by `GET /api/join-requests/:id`. */
export type JoinRequestStatusMessage =
  | { status: 'pending' }
  | { status: 'approved'; playerId: string; playerToken: string; code: string }
  | { status: 'rejected' };
